import { cn } from '@cannect/lib/utils'
import { Button, Form, ScrollArea, Separator, Sheet, Typography } from '@cannect/new-components/atoms'
import { CustomSelect } from '@cannect/new-components/atoms/Form/CustomSelect'
import { CounterBadge } from '@cannect/new-components/molecules/CounterBadge'
import { ProductFilters, ProductListItem } from '@cannect/new-components/organisms'
import { CategoryCards, SubcategoryCircles } from '@cannect/new-components/organisms/Marketplace'
import { PromoBanner } from '@cannect/new-components/organisms/Marketplace/PromoBanner/PromoBanner'
import { useProductLayout } from '@cannect/new-components/organisms/ProductListItem/useProductLayout'
import { TProductFiltersByNames } from '@cannect/services/resources/products'
import { Menu, Transition } from '@headlessui/react'
import EmptyState from 'components/EmptyState'
import LoadingContainer from 'components/LoadingContainer'
import Pagination from 'components/Pagination'
import SEO from 'components/SEO'
import { Check, ChevronDown } from 'lucide-react'
import { Fragment } from 'react'
import { LuGrid, LuList, LuSearch } from 'react-icons/lu'
import { Link } from 'react-router-dom'
import When from 'utils/when'
import ActionCards from '@cannect/new-components/ActionCards/ActionCards'
import { useProductsList } from './useProductsList'

type TProductsListUI = ReturnType<typeof useProductsList>

const LayoutDropdown = ({
  currentLayout,
  onChange
}: {
  currentLayout: 'grid' | 'list'
  onChange: (value: 'grid' | 'list') => void
}) => {
  const layoutOptions = [
    { value: 'grid', label: 'Em Grade', icon: LuGrid },
    { value: 'list', label: 'Em Lista', icon: LuList }
  ]

  const selected = layoutOptions.find((opt) => opt.value === currentLayout)

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        className="relative inline-flex h-9 w-[158px] items-center justify-between rounded-md bg-white px-2 py-2 text-sm font-normal text-neutral-700 hover:bg-gray-50"
        style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}>
        {/* Esquerda: ícone */}
        <span className="flex items-center text-neutral-500">{selected?.icon && <selected.icon size={16} />}</span>

        {/* Centro: label absolutamente centralizado */}
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-neutral-500">
          {selected?.label}
        </span>

        {/* Direita: chevron */}
        <ChevronDown size={16} className="text-neutral-500" />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95">
        <Menu.Items
          className="absolute right-0 z-10 mt-2 origin-top-right rounded-md bg-white text-neutral-500 shadow-lg focus:outline-none"
          style={{ border: '1px solid var(--Neutrals-Neutrals100, #E3E3E3)' }}>
          {layoutOptions.map((option) => (
            <Menu.Item key={option.value}>
              {({ active }) => (
                <button
                  onClick={() => onChange(option.value as 'grid' | 'list')}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 border-0 px-4 py-2 text-sm text-neutral-500',
                    active && 'bg-gray-100'
                  )}>
                  <div className="flex items-center gap-2">
                    <option.icon size={16} />
                    {option.label}
                  </div>
                  {currentLayout === option.value && <Check size={14} className="text-primary-600" />}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Transition>
    </Menu>
  )
}

export const ProductsListUI = ({
  products,
  filters,
  totalCount,
  totalPages,
  currentPage,
  isLoading,
  isFetching,
  isFlowerBannerVisible,
  search,
  currentSortOrder,
  currentLayout,
  isMedicationCategoryActive,
  sortOptions,
  isCannabisListing,
  isFiltersVisible,
  setIsFiltersVisible,
  isAuthenticated,
  isMobile,
  activeFiltersCount,
  handleSearchChange,
  handlePageChange,
  handleSortChange,
  handleLayoutChange
}: TProductsListUI) => {
  const { layout } = useProductLayout()

  const getGridClasses = () => {
    if (layout === 'list') {
      return 'flex flex-col justify-items-center gap-x-4 gap-y-4'
    }
    if (isFiltersVisible) {
      return 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 justify-items-center gap-x-4 gap-y-4'
    }
    return 'grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 justify-items-center gap-x-4 gap-y-4'
  }

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <SEO
        title="Produtos de Cannabis Medicinal"
        description="Encontre produtos de cannabis medicinal de alta qualidade. Óleos, cápsulas, cremes e mais. Marcas nacionais e importadas com entrega em todo Brasil."
        canonical="/produtos/1"
        keywords="produtos cannabis medicinal, óleo CBD, canabidiol, THC, óleos medicinais, cápsulas cannabis"
      />
      {isFlowerBannerVisible && (
        <div className="mb-6 border-l-4 border-yellow-500 bg-yellow-100 p-4">
          <Typography.Text className="text-yellow-800">
            ⚠️ Produtos de flores disponíveis - Entre em contato via WhatsApp
          </Typography.Text>
        </div>
      )}

      <div className="container mx-auto px-4 py-6">
        <CategoryCards />
        {/* O banner promocional é da cannabis medicinal. As demais categorias e as coleções da
            farmácia Pill ficam sem ele; Medicamento segue trocando o banner pelos círculos. */}
        {isMedicationCategoryActive && <SubcategoryCircles />}
        {!isMedicationCategoryActive && isCannabisListing && <PromoBanner />}

        {isFiltersVisible ? (
          <div
            className={cn(
              'mt-10 grid grid-cols-1 gap-6 lg:grid-cols-4',
              !isLoading && products.length === 0 && 'lg:items-start'
            )}>
            <div className="lg:col-span-1">
              {isMobile ? (
                <Sheet.Root modal={false}>
                  <Sheet.Trigger asChild>
                    <Button variant="outline" className="relative w-full gap-2">
                      <span>Filtros</span>
                      {activeFiltersCount > 0 && <CounterBadge quantity={activeFiltersCount} />}
                    </Button>
                  </Sheet.Trigger>

                  <Sheet.Content onInteractOutside={(e) => e.preventDefault()} className="pb-[120px]">
                    <Sheet.Header className="mt-8">
                      <Sheet.Title>Filtros de Produtos</Sheet.Title>
                      <Sheet.Description>Selecione os filtros para encontrar os produtos ideais</Sheet.Description>
                    </Sheet.Header>

                    <Separator className="my-4" />

                    <ScrollArea className="h-[calc(100%-120px)] pr-2">
                      <ProductFilters filters={filters as TProductFiltersByNames} />
                    </ScrollArea>

                    <Sheet.Footer className="h-[120px] items-center justify-end gap-3 px-6 py-4">
                      <Sheet.Close asChild>
                        <Button size="lg" full>
                          Aplicar Filtros
                        </Button>
                      </Sheet.Close>
                    </Sheet.Footer>
                  </Sheet.Content>
                </Sheet.Root>
              ) : (
                <ProductFilters
                  filters={filters as TProductFiltersByNames}
                  onFiltersVisibilityChange={setIsFiltersVisible}
                  isFiltersVisible={isFiltersVisible}
                  hideToggleButton={false}
                />
              )}
            </div>

            <div className="lg:col-span-3">
              <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="flex flex-1 flex-col gap-2">
                  <Typography.Text className="[&&]:text-gray-600">
                    Não encontrou seu produto?{' '}
                    <Link to="/jornada-digital" className="[&&]:text-primary-800">
                      Envie aqui sua receita e documentos.
                    </Link>
                  </Typography.Text>
                  <div className="flex-1">
                    <Form.Input
                      size="md"
                      className="rounded-full bg-white"
                      placeholder="Buscar produtos..."
                      value={search}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      variant="search"
                      rigthElement={<LuSearch />}
                    />
                  </div>
                </div>

                <div className="flex flex-shrink-0 flex-col items-end gap-2">
                  <Typography.Text className="whitespace-nowrap text-sm text-gray-500">
                    {products.length} de {totalCount} resultado{totalCount > 1 ? 's' : ''}
                  </Typography.Text>

                  <div className="flex w-fit flex-shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                    <CustomSelect
                      size="small"
                      placeholder="Ordenar por"
                      options={sortOptions}
                      value={sortOptions.find((option) => option.value === currentSortOrder) || null}
                      onChange={handleSortChange}
                      customClassNames={{
                        container: 'w-full'
                      }}
                    />

                    {!isMobile && isAuthenticated && (
                      <LayoutDropdown
                        currentLayout={currentLayout}
                        onChange={(value) => handleLayoutChange({ value: value as 'grid' | 'list' })}
                      />
                    )}
                  </div>
                </div>
              </div>

              <When expr={isLoading}>
                <LoadingContainer loading />
              </When>

              <When expr={!isLoading && products.length === 0}>
                <div className="flex h-[450px]">
                  <EmptyState title="Nenhum produto encontrado" withAnimation />
                </div>
              </When>

              <When expr={!isLoading && products.length > 0}>
                <div className="mb-8" data-product-sections>
                  <div className={getGridClasses()}>
                    {products.map((product: any) => (
                      <ProductListItem key={product.id} product={product} variant={layout as 'grid' | 'list'} />
                    ))}
                  </div>
                </div>

                {totalPages > 1 && (
                  <div className="flex justify-center pt-4">
                    <Pagination
                      containerClassName="[&&]:justify-end"
                      forcePage={currentPage - 1}
                      onPageChange={({ selected }: any) => handlePageChange(selected + 1)}
                      pageCount={totalPages}
                    />
                  </div>
                )}
              </When>

              {isFetching && !isLoading && (
                <div className="flex justify-center py-4">
                  <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600" />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-10 w-full">
            <div
              className={cn(
                'grid grid-cols-1 gap-6 lg:grid-cols-4',
                !isLoading && products.length === 0 && 'lg:items-start'
              )}>
              <div className="lg:col-span-1">
                <ProductFilters
                  filters={filters as TProductFiltersByNames}
                  onFiltersVisibilityChange={setIsFiltersVisible}
                  isFiltersVisible={isFiltersVisible}
                  showOnlyButton={isMobile}
                />
              </div>

              <div className="lg:col-span-3">
                <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex-1">
                    <div className="mb-2">
                      <Typography.Text className="[&&]:text-gray-600">
                        Não encontrou seu produto?{' '}
                        <Link to="/jornada-digital" className="[&&]:text-primary-800">
                          Envie aqui sua receita e documentos.
                        </Link>
                      </Typography.Text>
                    </div>
                    <div className="mr-4 flex-1">
                      <Form.Input
                        size="md"
                        className="rounded-full bg-white"
                        placeholder="Buscar produtos..."
                        value={search}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        variant="search"
                        rigthElement={<LuSearch />}
                      />
                    </div>
                  </div>

                  <div className="flex w-fit flex-shrink-0 flex-col gap-2">
                    <Typography.Text className="text-sm text-gray-500">
                      {products.length} de {totalCount} resultado{totalCount > 1 ? 's' : ''}
                    </Typography.Text>

                    <div className="flex w-fit flex-shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                      <CustomSelect
                        size="small"
                        placeholder="Ordenar por"
                        options={sortOptions}
                        value={sortOptions.find((option) => option.value === currentSortOrder) || null}
                        onChange={handleSortChange}
                        customClassNames={{
                          container: 'w-full'
                        }}
                      />

                      {!isMobile && isAuthenticated && (
                        <LayoutDropdown
                          currentLayout={currentLayout}
                          onChange={(value) => handleLayoutChange({ value: value as 'grid' | 'list' })}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full">
              <When expr={isLoading}>
                <LoadingContainer loading />
              </When>

              <When expr={!isLoading && products.length === 0}>
                <div className="flex h-[450px]">
                  <EmptyState title="Nenhum produto encontrado" withAnimation />
                </div>
              </When>

              <When expr={!isLoading && products.length > 0}>
                <div className="mb-8" data-product-sections>
                  <div className={getGridClasses()}>
                    {products.map((product: any) => (
                      <ProductListItem key={product.id} product={product} variant={layout as 'grid' | 'list'} />
                    ))}
                  </div>
                </div>

                {totalPages > 1 && (
                  <div className="flex justify-center pt-4">
                    <Pagination
                      containerClassName="[&&]:justify-end"
                      forcePage={currentPage - 1}
                      onPageChange={({ selected }: any) => handlePageChange(selected + 1)}
                      pageCount={totalPages}
                    />
                  </div>
                )}
              </When>

              {isFetching && !isLoading && (
                <div className="flex justify-center py-4">
                  <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600" />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Seções de Agendamento e Envio de Receita */}
        <ActionCards />
      </div>
    </div>
  )
}
